import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

const ROUTE_TITLES: Record<string, string> = {
  '/': 'Home | R-DAP',
  '/docs': 'Documentation | R-DAP',
  '/announcements': 'Announcements | R-DAP',
  '/announcements/admin': 'Announcements Admin | R-DAP',
  '/notifications': 'Notifications | R-DAP',
};

// TODO: replace this temporary route map with dynamic page-title ownership.
// In particular, TemplateGroup must render visual headings without allowing
// ContentHeader to overwrite the browser title asynchronously.

const titleForPath = (pathname: string): string | undefined => {
  const directTitle = ROUTE_TITLES[pathname];
  if (directTitle) {
    return directTitle;
  }

  if (pathname === '/create' || pathname.startsWith('/create/')) {
    return 'Create | R-DAP';
  }

  return undefined;
};

/**
 * Owns the browser title for pages whose frontend extension does not expose
 * a reliable title to the document head.
 *
 * The route check is intentional: TechDocs reader pages and other
 * plugin-owned child routes retain their own contextual titles.
 */
export const RouteTitle = ({ children }: { children: ReactNode }) => {
  const location = useLocation();

  useEffect(() => {
    const title = titleForPath(location.pathname);
    if (!title) {
      return undefined;
    }

    const applyTitle = () => {
      if (document.title !== title) {
        document.title = title;
      }
    };

    applyTitle();

    const titleElement = document.querySelector('title');
    if (!titleElement) {
      return undefined;
    }

    // Some Backstage pages update <title> asynchronously after their data
    // loads. Keep ownership only while an explicitly mapped route is active.
    const observer = new MutationObserver(applyTitle);
    observer.observe(titleElement, {
      childList: true,
      characterData: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [location.pathname]);

  return <>{children}</>;
};
